import { Card, Tag, Title, Text, Icon } from '@nimbus-ds/components'
import { StoreIcon } from '@nimbus-ds/icons'
import { formatCurrency } from '@/lib/utils'

// Off-storefront orders (POS / manual / marketplace / API) that the analytics pipeline segregates OUT
// of the web conversion rate, surfaced here so the exclusion is transparent — a real concern for
// POS-heavy merchants whose in-store sales would otherwise look "lost". See
// docs/specs/OFF-STOREFRONT-ORDERS-SPEC.md §5/§7 (S4 — Surface).

export interface OffStorefrontOrders {
  count: number
  revenue: number
  by_storefront: Record<string, { count: number; revenue: number }>
}

export interface StorefrontSummary {
  distribution?: Record<string, number>
  resolved_by?: Record<string, number>
  unknown_values?: string[]
  web_conversions?: number
  off_storefront_conversions?: number
}

const TYPE_LABELS: Record<string, string> = {
  pos: 'Local / POS',
  form: 'Manuales (borrador)',
  meli: 'Mercado Libre',
  api: 'API / app',
  // web values (only appear in the consultant distribution, never as excluded types)
  store: 'Tienda (escritorio)',
  mobile: 'Tienda (celular)',
  null: 'Sin dato',
}
const label = (k: string) => TYPE_LABELS[k] || k

/**
 * @param detailed consultant tier — adds the storefront monitor (distribution / resolved_by /
 *   unknown_values). Merchant tier (default) shows only the calm count/revenue/by-type summary.
 */
export function OffStorefrontCard({
  off,
  summary,
  detailed = false,
}: {
  off?: OffStorefrontOrders | null
  summary?: StorefrontSummary | null
  detailed?: boolean
}) {
  if (!off || off.count <= 0) return null

  const types = Object.entries(off.by_storefront || {}).sort((a, b) => b[1].count - a[1].count)

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Icon source={<StoreIcon />} />
          <Title as="h4">Pedidos no-web (excluidos)</Title>
        </div>
        <Text>
          Ventas reales que no pasaron por tu tienda online — no cuentan en la tasa de conversión web.
        </Text>
      </Card.Header>
      <Card.Body>
        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{off.count}</Text>
            <Text>
              {off.count === 1 ? 'pedido' : 'pedidos'} · {formatCurrency(off.revenue)}
            </Text>
          </div>

          <div className="flex flex-wrap gap-2">
            {types.map(([k, v]) => (
              <Tag key={k} appearance="neutral">
                {label(k)}: {v.count} ({formatCurrency(v.revenue)})
              </Tag>
            ))}
          </div>

          {detailed && summary && (
            <div className="mt-2 space-y-1 border-t pt-3">
              <Text fontSize="caption" color="neutral-textHigh" fontWeight="medium">Detalle (consultor)</Text>
              {summary.distribution && Object.keys(summary.distribution).length > 0 && (
                <Text fontSize="caption">
                  Distribución storefront:{' '}
                  {Object.entries(summary.distribution)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, n]) => `${k}=${n}`)
                    .join(', ')}
                </Text>
              )}
              {summary.resolved_by && (
                <Text fontSize="caption">
                  Resolución: {Object.entries(summary.resolved_by).map(([k, n]) => `${k}=${n}`).join(', ')}
                </Text>
              )}
              <Text fontSize="caption">
                Conversiones web {summary.web_conversions ?? 0} · off-storefront {summary.off_storefront_conversions ?? 0}
              </Text>
              {summary.unknown_values && summary.unknown_values.length > 0 && (
                <div className="text-xs text-amber-600">
                  ⚠ Valores storefront desconocidos: {summary.unknown_values.join(', ')} — clasificar con soporte TN.
                </div>
              )}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}
